// ============================================================================
// GeoLocateButton.tsx — кнопка «Где я» (единая точка определения местоположения).
// Использует существующий механизм lib/geolocation.ts (captureLocation):
// явные ошибки (отказ/таймаут) — тост + журнал регистрации, без заглушек.
// ============================================================================

import { useState } from 'react';
import { Crosshair, Loader2 } from 'lucide-react';
import { captureLocation, type GeoPoint } from '@/lib/geolocation';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import { useToastStore } from '@/store/toastStore';

interface GeoLocateButtonProps {
  onLocated: (point: GeoPoint) => void;
  className?: string;
  title?: string;
  timeoutMs?: number;
}

export function GeoLocateButton({ onLocated, className, title, timeoutMs }: GeoLocateButtonProps) {
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const point = await captureLocation({ timeoutMs });
      onLocated(point);
    } catch (e) {
      logErrorToJournal(e, 'Определение местоположения');
      useToastStore.getState().showError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      className={className || 'h-8 w-8 rounded-full flex items-center justify-center border border-border bg-card/95 backdrop-blur shadow-sm hover:bg-accent transition-colors'}
      title={title || 'Где я — определить моё местоположение'}
      disabled={busy}
      onClick={() => void run()}
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crosshair className="h-4 w-4" />}
    </button>
  );
}
